import { apiFetch } from '../../../api/client';
import { parseJsonResponse } from '../../../api/parseResponse';

export type WaConnectionState = 'disconnected' | 'connecting' | 'qr_ready' | 'connected' | 'reconnecting' | 'error';

export interface WhatsAppStatus {
  session: {
    status: WaConnectionState;
    phone: string | null;
    display_name: string | null;
    connected_at: string | null;
    last_seen_at: string | null;
  };
  assistant: { auto_reply_enabled: boolean; state: 'active' | 'paused' };
  gateway: { healthy: boolean };
}

export interface GeneralSettings {
  assistant_status: 'on' | 'off';
  assistant_name: string;
}

export interface AgentPolicies {
  allow: string[];
  deny: string[];
  require_human: string[];
}

export interface AgentStatusRow {
  id: number;
  role: string;
  assistant_key: string | null;
  name: string;
  status: string;
  handled_today: number;
  personality: string | null;
  mission: string | null;
  department: string | null;
  owner_user_id: number | null;
  capabilities: string[];
  policies: AgentPolicies | null;
  handoff_targets: string[];
  channels: string[];
}

export interface AgentActivityRow {
  id: number;
  assistant_id: number | null;
  conversation_id: number | null;
  customer_id: number | null;
  tool: string;
  action: string;
  result: string;
  created_at: string;
}

const BASE = '/platform/smart-assistant/settings';

async function get<T>(path: string) {
  return parseJsonResponse<T>(await apiFetch(path));
}

async function post<T>(path: string, body: Record<string, unknown> = {}) {
  return parseJsonResponse<T>(await apiFetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  }));
}

async function put<T>(path: string, body: Record<string, unknown>) {
  return parseJsonResponse<T>(await apiFetch(path, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  }));
}

/** Independent adapter — components never call fetch directly. */
export const smartAssistantSettingsApi = {
  getWhatsAppStatus: () => get<WhatsAppStatus>(`${BASE}/whatsapp/status`),
  getWhatsAppQr: () => get<{ qr: string | null; status: WaConnectionState }>(`${BASE}/whatsapp/qr`),
  connectWhatsApp: () => post<{ status: WaConnectionState }>(`${BASE}/whatsapp/connect`),
  reconnectWhatsApp: () => post<{ status: WaConnectionState }>(`${BASE}/whatsapp/reconnect`),
  disconnectWhatsApp: () => post<{ status: WaConnectionState }>(`${BASE}/whatsapp/disconnect`),
  testWhatsApp: (to: string) => post<{ sent: boolean }>(`${BASE}/whatsapp/test`, { to }),
  getGeneralSettings: () => get<GeneralSettings>(`${BASE}/general`),
  updateGeneralSettings: (data: Partial<GeneralSettings>) => put<GeneralSettings>(`${BASE}/general`, data),
  resetAllSessions: () => post<{
    whatsapp_conversations: number;
    intelligence_conversations: number;
    memory_items: number;
    reset_at: string;
  }>(`${BASE}/reset-sessions`),
  getAgents: () => get<AgentStatusRow[]>(`${BASE}/agents`),
  createAgent: (data: Record<string, unknown>) => post<{ id: number; assistant_key: string }>(`${BASE}/agents`, data),
  updateAgent: (id: number, data: Record<string, unknown>) => put<AgentStatusRow>(`${BASE}/agents/${id}`, data),
  setAgentStatus: (id: number, status: 'active' | 'paused') =>
    parseJsonResponse<AgentStatusRow>(apiFetch(`${BASE}/agents/${id}/status`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    })),
  getAgentActivity: (id: number) => get<AgentActivityRow[]>(`${BASE}/agents/${id}/activity`),
};
